"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthContext } from "@/lib/auth-context"
import { useUser } from "@/lib/community/hooks/use-user"
import { toggleFollow } from "@/lib/community/service/interaction-service"
import { useToast } from "@/lib/hooks/use-toast"
import { ApiError } from "@/lib/api"
import { CommunityAvatar } from "./community-avatar"
import { formatCount } from "@/lib/community/utils"

const HOVER_DELAY = 400
const LEAVE_DELAY = 200

interface AuthorHoverCardProps {
  authorId: string
  children: React.ReactNode
}

export function AuthorHoverCard({ authorId, children }: AuthorHoverCardProps) {
  const navigate = useNavigate()
  const { currentUser, isLoggedIn, openLogin } = useAuthContext()
  const [visible, setVisible] = useState(false)
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { user, loading } = useUser(visible ? authorId : undefined)
  const toast = useToast()

  const isSelf = currentUser && authorId === currentUser.userId

  const handleEnter = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current)
      leaveTimer.current = null
    }
    enterTimer.current = setTimeout(() => {
      setVisible(true)
    }, HOVER_DELAY)
  }, [])

  const handleLeave = useCallback(() => {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current)
      enterTimer.current = null
    }
    leaveTimer.current = setTimeout(() => {
      setVisible(false)
    }, LEAVE_DELAY)
  }, [])

  useEffect(() => {
    return () => {
      if (enterTimer.current) clearTimeout(enterTimer.current)
      if (leaveTimer.current) clearTimeout(leaveTimer.current)
    }
  }, [])

  const handleFollow = useCallback(async () => {
    if (!isLoggedIn) {
      openLogin()
      return
    }
    if (!user || isSelf) return
    try {
      const nowFollowed = await toggleFollow(authorId)
      toast.success(nowFollowed ? "关注成功" : "已取消关注")
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return
      toast.error(err instanceof ApiError ? err.statusText : "操作失败，请重试")
    }
  }, [isLoggedIn, openLogin, user, isSelf, authorId, toast])

  const goToProfile = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const target = isSelf ? "/community/user/u-self" : `/community/user/${authorId}`
    navigate(target)
  }

  return (
    <div
      ref={containerRef}
      className="relative inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}

      {visible && (
        <div
          className="absolute left-0 top-full z-50 mt-1.5 w-[320px] rounded-xl border border-border/30 bg-background/95 backdrop-blur-xl shadow-xl shadow-black/10 animate-in fade-in slide-in-from-top-1 duration-150"
          onMouseEnter={() => {
            if (leaveTimer.current) {
              clearTimeout(leaveTimer.current)
              leaveTimer.current = null
            }
          }}
          onMouseLeave={handleLeave}
          onClick={(e) => e.stopPropagation()}
        >
          {loading && !user ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          ) : user ? (
            <div>
              {/* 头部：头像 + 名称 + bio */}
              <div className="p-4 pb-3">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={goToProfile} className="shrink-0">
                    <CommunityAvatar src={user.avatar} name={user.displayName} sizeClass="w-14 h-14" />
                  </button>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <button
                      type="button"
                      onClick={goToProfile}
                      className="text-[15px] font-bold text-foreground hover:text-primary transition-colors truncate block max-w-full"
                    >
                      {user.displayName}
                    </button>
                    <p className="text-xs text-muted-foreground/60 mt-0.5 line-clamp-2 leading-relaxed">
                      {user.bio || "这个人很懒，什么都没写"}
                    </p>
                  </div>
                </div>
              </div>

              {/* 分割线 */}
              <div className="border-t border-border/20 mx-4" />

              {/* 统计数据 */}
              <div className="grid grid-cols-3 gap-2 px-4 py-3">
                <div className="text-center">
                  <div className="text-[15px] font-bold text-foreground">{formatCount(user.stats.posts)}</div>
                  <div className="text-[11px] text-muted-foreground/50">发布</div>
                </div>
                <div className="text-center">
                  <div className="text-[15px] font-bold text-foreground">{formatCount(user.stats.totalLikes)}</div>
                  <div className="text-[11px] text-muted-foreground/50">获赞</div>
                </div>
                <div className="text-center">
                  <div className="text-[15px] font-bold text-foreground">{formatCount(user.stats.followers)}</div>
                  <div className="text-[11px] text-muted-foreground/50">关注者</div>
                </div>
              </div>

              {/* 分割线 */}
              <div className="border-t border-border/20 mx-4" />

              {/* 操作按钮 */}
              {!isSelf && (
                <div className="flex gap-2 p-4 pt-3">
                  <button
                    type="button"
                    onClick={handleFollow}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                      user.isFollowed
                        ? "bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                  >
                    {user.isFollowed ? "已关注" : "+ 关注"}
                  </button>
                  <button
                    type="button"
                    onClick={goToProfile}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium border border-border/40 text-foreground/70 hover:bg-foreground/5 transition-colors"
                  >
                    查看主页
                  </button>
                </div>
              )}
              {isSelf && (
                <div className="flex gap-2 p-4 pt-3">
                  <button
                    type="button"
                    onClick={goToProfile}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium border border-border/40 text-foreground/70 hover:bg-foreground/5 transition-colors"
                  >
                    查看我的主页
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
